import { useCallback } from 'react';

/**
 * Custom hook that transforms onChange behavior for required text fields.
 *
 * When a required field is cleared (becomes empty or whitespace-only),
 * this hook transforms the empty string to `undefined` so RJSF will
 * trigger the "required" validation error.
 *
 * @param originalOnChange - The original onChange handler from RJSF widget props
 * @param required - Whether this field is marked as required in the schema
 * @returns A transformed onChange handler that converts empty strings to undefined for required fields
 */
export const useRequiredFieldOnChange = <T = string>(
  originalOnChange?: (value: T | undefined) => void,
  required = false,
): ((value: T) => void) =>
  useCallback(
    (value: T) => {
      if (!originalOnChange) return;

      if (required && typeof value === 'string' && value.trim() === '') {
        originalOnChange(undefined);
      } else {
        originalOnChange(value);
      }
    },
    [
      originalOnChange,
      required,
    ],
  );
